import type { BreadcrumbItem as ViewBreadcrumbItem } from "@/components/parts/Breadcrumb";
import { COMMON_BREADCRUMB_ITEMS } from "@/constants/Site";

export interface BreadcrumbItem {
  name: string;
  url?: string;
}

/**
 * パンくずリスト構築のためのユーティリティ関数
 * 重複コードを排除し、一貫したパンくずリストを生成します
 */

/**
 * ごはん量計算ページのパンくずリストを取得
 */
export function getCalorieBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.meal,
    COMMON_BREADCRUMB_ITEMS.calorie,
  ];
}

/**
 * ごはん検索ページのパンくずリストを取得
 */
export function getFoodsBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.meal,
    COMMON_BREADCRUMB_ITEMS.foods,
  ];
}

/**
 * ごはん詳細の計算ページのパンくずリストを取得
 */
export function getFoodBreadcrumb(
  foodName: string | null,
  url: string,
): BreadcrumbItem[] {
  return appendBreadcrumbItem(getFoodsBreadcrumb(), {
    name: foodName ?? COMMON_BREADCRUMB_ITEMS.calorie.name,
    url,
  });
}

/**
 * ごはん切り替えページのパンくずリストを取得
 */
export function getTransitionBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.meal,
    COMMON_BREADCRUMB_ITEMS.transition,
  ];
}

/**
 * 動画一覧ページのパンくずリストを取得
 */
export function getVideosBreadcrumb(tag: unknown): BreadcrumbItem[] {
  if (tag === "learning") {
    return [
      COMMON_BREADCRUMB_ITEMS.home,
      COMMON_BREADCRUMB_ITEMS.media,
      COMMON_BREADCRUMB_ITEMS.learningVideos,
    ];
  }

  if (tag === "healing") {
    return [
      COMMON_BREADCRUMB_ITEMS.home,
      COMMON_BREADCRUMB_ITEMS.media,
      COMMON_BREADCRUMB_ITEMS.healingVideos,
    ];
  }

  return [COMMON_BREADCRUMB_ITEMS.home, COMMON_BREADCRUMB_ITEMS.videos];
}

/**
 * 年齢換算ページのパンくずリストを取得
 */
export function getAgeBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.health,
    COMMON_BREADCRUMB_ITEMS.age,
  ];
}

/**
 * マイデータページのパンくずリストを取得
 */
export function getMyDataBreadcrumb(): BreadcrumbItem[] {
  return [COMMON_BREADCRUMB_ITEMS.home, COMMON_BREADCRUMB_ITEMS.mydata];
}

/**
 * プライバシーポリシーページのパンくずリストを取得
 */
export function getPrivacyBreadcrumb(): BreadcrumbItem[] {
  return [COMMON_BREADCRUMB_ITEMS.home, COMMON_BREADCRUMB_ITEMS.privacy];
}

/**
 * 猫を迎える費用ページのパンくずリストを取得
 */
export function getCostBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.welcome,
    COMMON_BREADCRUMB_ITEMS.cost,
  ];
}

/**
 * 年齢換算ガイドページ（/age/{年齢}/）のパンくずリストを取得
 */
export function getAgeYearGuideBreadcrumb(
  years: number,
  url: string,
): BreadcrumbItem[] {
  return appendBreadcrumbItem(getAgeBreadcrumb(), {
    name: `${years}歳の猫`,
    url,
  });
}

/**
 * 記事一覧ページのパンくずリストを取得
 */
export function getArticlesBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.media,
    COMMON_BREADCRUMB_ITEMS.articles,
  ];
}

/**
 * 記事詳細ページのパンくずリストを取得
 */
export function getArticleBreadcrumb(
  title: string,
  url: string,
): BreadcrumbItem[] {
  return appendBreadcrumbItem(getArticlesBreadcrumb(), { name: title, url });
}

/**
 * 保護猫ガイドページのパンくずリストを取得
 */
export function getAdoptionBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.welcome,
    COMMON_BREADCRUMB_ITEMS.adoption,
  ];
}

/**
 * 都道府県ごとの保護猫案内ページのパンくずリストを取得
 */
export function getAdoptionPrefectureBreadcrumb(
  prefecture: string,
  url: string,
): BreadcrumbItem[] {
  return appendBreadcrumbItem(getAdoptionBreadcrumb(), {
    name: prefecture,
    url,
  });
}

/**
 * 体型チェックページのパンくずリストを取得
 */
export function getBodyConditionBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.health,
    COMMON_BREADCRUMB_ITEMS.bodyCondition,
  ];
}

/**
 * 猫が食べても大丈夫か調べるページのパンくずリストを取得
 */
export function getFoodsSafetyBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.health,
    COMMON_BREADCRUMB_ITEMS.foodsSafety,
  ];
}

/**
 * 猫のイベントを探すページのパンくずリストを取得
 */
export function getEventsBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.media,
    COMMON_BREADCRUMB_ITEMS.events,
  ];
}

/**
 * 年ごとの猫のイベント一覧ページのパンくずリストを取得
 */
export function getEventsYearBreadcrumb(
  year: number,
  url: string,
): BreadcrumbItem[] {
  return appendBreadcrumbItem(getEventsBreadcrumb(), {
    name: `${String(year)}年`,
    url,
  });
}

/**
 * 猫が食べても大丈夫か調べる詳細ページのパンくずリストを取得
 */
export function getFoodSafetyDetailBreadcrumb(
  name: string,
  url: string,
): BreadcrumbItem[] {
  return appendBreadcrumbItem(getFoodsSafetyBreadcrumb(), { name, url });
}

/**
 * はじめて猫を飼う暮らしを体験するゲームのパンくずリストを取得
 */
export function getFirstCatGameBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.game,
    COMMON_BREADCRUMB_ITEMS.firstCatGame,
  ];
}

/**
 * 猫として一生を生きるゲーム（/game/nekosei/）のパンくずリストを取得
 */
export function getNekoseiGameBreadcrumb(): BreadcrumbItem[] {
  return [
    COMMON_BREADCRUMB_ITEMS.home,
    COMMON_BREADCRUMB_ITEMS.game,
    COMMON_BREADCRUMB_ITEMS.nekoseiGame,
  ];
}

/**
 * 構造化データ用のパンくずを画面表示用へ変換
 */
export function toViewBreadcrumbItems(
  items: BreadcrumbItem[],
): ViewBreadcrumbItem[] {
  return items.map((item, index) => {
    const url = item.url ? new URL(item.url) : undefined;

    return {
      label: item.name,
      href:
        index === items.length - 1 || url === undefined
          ? undefined
          : `${url.pathname}${url.search}${url.hash}`,
    };
  });
}

/**
 * パンくずリストに追加のアイテムを追加
 */
export function appendBreadcrumbItem(
  baseItems: BreadcrumbItem[],
  item: BreadcrumbItem,
): BreadcrumbItem[] {
  return [...baseItems, item];
}

/**
 * 利用規約ページのパンくずリストを取得
 */
export function getTermsBreadcrumb(): BreadcrumbItem[] {
  return [COMMON_BREADCRUMB_ITEMS.home, COMMON_BREADCRUMB_ITEMS.terms];
}
